/**
 * Risk & Compliance stored `type` catalogue.
 *
 * Mirrors `ASSET_EVENT_TYPES` (`src/helper-functions/events/asset-register/asset-event-types.js`):
 * one camelCase `type` per verb, one `detail` branch each, and the API validates neither - this
 * catalogue is the frontend convention that keeps the ledger readable.
 *
 * Shape and retention: `devdocs/features/risk-compliance/design/risk-compliance-schema.md` §3.
 */

export const RETENTION_CLASSES = { INDEFINITE: 'indefinite', SEVEN_YEAR: 'sevenYear' }

const withRetention = (defs, retention) => Object.fromEntries(Object.entries(defs).map(([key, def]) => [key, { ...def, retention: def.retention || retention }]))

export const RISK_COMPLIANCE_EVENT_TYPES = {
    ...withRetention(
        {
            riskRecord: { type: 'riskRecord', branch: 'risk', subtypes: ['identify', 'update', 'close'], effect: 'A risk exists, is re-rated, or is closed' },
        },
        RETENTION_CLASSES.INDEFINITE,
    ),
    ...withRetention(
        {
            riskControl: { type: 'riskControl', branch: 'control', subtypes: ['add', 'update', 'retire'], effect: 'A control against a risk changes' },
        },
        RETENTION_CLASSES.SEVEN_YEAR,
    ),
    ...withRetention(
        {
            assessmentRun: { type: 'assessmentRun', branch: 'assessment', subtypes: ['scheme', 'guided', 'scan'], effect: 'A question set was answered - scheme, guided wizard, or holistic scan' },
        },
        RETENTION_CLASSES.INDEFINITE,
    ),
    ...withRetention(
        {
            schemeEnrol: { type: 'schemeEnrol', branch: 'scheme', subtypes: ['join', 'renew', 'leave'], effect: "A scheme membership changes, activating that pack's obligations" },
            obligationRecord: { type: 'obligationRecord', branch: 'obligation', subtypes: ['open', 'update', 'waive', 'close'], effect: 'An obligation is opened, changed, waived or closed' },
            obligationMet: { type: 'obligationMet', branch: 'met', effect: 'Evidence that an obligation was satisfied for a period' },
        },
        RETENTION_CLASSES.SEVEN_YEAR,
    ),
    ...withRetention(
        {
            auditRecord: { type: 'auditRecord', branch: 'audit', effect: 'An external or internal audit and its findings' },
        },
        RETENTION_CLASSES.INDEFINITE,
    ),
    // incidentRecord's class follows the schema doc's stated default ("the rest follow the 7-year
    // purge") as it reads today - CR-RSK-04's "kept indefinitely" case names
    // assessmentRun/riskRecord/auditRecord specifically and has not been re-derived for incidents.
    // A near-miss or an adverse result may deserve the same indefinite class; open, not decided.
    ...withRetention(
        {
            incidentRecord: { type: 'incidentRecord', branch: 'incident', subtypes: ['report', 'investigate', 'close'], effect: 'An incident, near miss, complaint or adverse result is reported, investigated or closed' },
        },
        RETENTION_CLASSES.SEVEN_YEAR,
    ),
    // Biosecurity records (`design/biosecurity-visitor-training-records.md`): proof of who came on, what
    // was cleaned and who was trained. The 7-year default until the retention catalogue (open question 7,
    // deferred with the purge) says otherwise.
    ...withRetention(
        {
            visitorRecord: { type: 'visitorRecord', branch: 'visitor', subtypes: ['signIn', 'signOut', 'update'], effect: 'A visitor signs in or out, or their entry record is corrected' },
            biosecurityCleaningRecord: { type: 'biosecurityCleaningRecord', branch: 'cleaning', subtypes: ['record', 'update'], effect: 'A vehicle, piece of equipment, footwear or clothing was cleaned or inspected' },
            trainingRecord: { type: 'trainingRecord', branch: 'training', subtypes: ['record', 'update'], effect: 'A person was trained, and with what result' },
        },
        RETENTION_CLASSES.SEVEN_YEAR,
    ),
    // Farm documents (`design/farm-documentation-tab.md`): which documents a farm keeps, their settings, and
    // each issued version with its content - so an old version re-exports exactly as it was issued.
    ...withRetention(
        {
            farmDocumentProfile: { type: 'farmDocumentProfile', branch: 'profile', subtypes: ['set'], effect: 'The farm-wide details documents use are set' },
            farmDocument: { type: 'farmDocument', branch: 'document', subtypes: ['enable', 'update', 'disable'], effect: 'A document is added to, changed in, or removed from the farm set' },
        },
        RETENTION_CLASSES.SEVEN_YEAR,
    ),
    // The retention setting is itself kept for good: what the farm said it keeps, and when it said so.
    ...withRetention(
        {
            retentionSetting: { type: 'retentionSetting', branch: 'retention', subtypes: ['set'], effect: 'The period a record class is kept for is set' },
        },
        RETENTION_CLASSES.INDEFINITE,
    ),
    ...withRetention(
        {
            farmDocumentVersion: { type: 'farmDocumentVersion', branch: 'version', subtypes: ['issue'], effect: 'A version of a farm document is issued' },
        },
        RETENTION_CLASSES.INDEFINITE,
    ),
}

export const riskComplianceEventTypeDef = type => RISK_COMPLIANCE_EVENT_TYPES[type] || null

export const isRiskComplianceEventType = type => Boolean(riskComplianceEventTypeDef(type))
